import { ContextualInfoButton, ContextualInfoButtonWithText } from "@/components/buttons/ContextualInfoButton";
import { downloadCsvFile } from "@/features/custom_downloads";
import { buildPortfolioExportExampleCsv, PortfolioExportExampleKey } from "@/tools/aggregate/portfolio-export/constants";
import { keys } from "@/tools/aggregate/portfolio-export/schema";
import { ArrowLeftOutlined, CloudDownloadOutlined } from "@ant-design/icons";
import { Button, Checkbox, Form, Row, Select, Switch } from "antd";
import { FormItemProps } from "antd/lib";
import { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { PortfolioRegionType } from "../types";
import styles from "./PortfolioExportConfigureTemplateForm.module.scss";
type ThresholdOption = {
  label: string;
  value: string;
  metric: string;
  note?: string;
  validation?: any;
};
const SwitchInput = ({
  disabled,
  onSwitchChange,
  ...props
}: FormItemProps & {
  disabled?: boolean;
  onSwitchChange?: (checked: boolean) => void;
}) => {
  return <Form.Item className={`mb-0 min-h-[52px] content-center items-center border-0 border-b !border-gray-200 px-4 py-2 ${styles.switchInput}`} name={props.name} colon={false} {...props} valuePropName="checked" data-sentry-element="unknown" data-sentry-component="SwitchInput" data-sentry-source-file="PortfolioExportConfigureTemplateForm.tsx">
      <Switch size="small" disabled={disabled} onChange={onSwitchChange} data-sentry-element="Switch" data-sentry-source-file="PortfolioExportConfigureTemplateForm.tsx" />
    </Form.Item>;
};
const SelectThresholdParameter = ({
  label,
  thresholdEnabled,
  setThresholdEnabled,
  options,
  info,
  onSelect,
  ...props
}: FormItemProps & {
  options: ThresholdOption[];
  info?: ReactNode;
  thresholdEnabled: boolean;
  setThresholdEnabled: (value: boolean) => void;
  onSelect: (value: string | null) => void;
}) => {
  const [selectedParameter, setSelectedParameter] = useState<ThresholdOption>(options[0]);
  const handleChange = (value: any) => {
    const selectedOption = options.find(option => option.value === value);
    if (!selectedOption) {
      return;
    }
    setSelectedParameter(selectedOption);
    onSelect(value);
  };
  return <div className={`${"itemClassNames"} flex justify-between border-0 border-b !border-gray-200 px-4 py-2`} data-sentry-component="SelectThresholdParameter" data-sentry-source-file="PortfolioExportConfigureTemplateForm.tsx">
      <Form.Item className={`mb-0 ${styles.switchInput}`} name={props.name} valuePropName="checked" colon={false} labelAlign="left" label={<div className="flex items-start gap-2">
            <div className="flex flex-1 items-start gap-1">
              <div>{label}</div>
              {selectedParameter?.metric && <span className="text-xs text-gray-500">({selectedParameter.metric})</span>}
            </div>
            <div>{info}</div>
          </div>} data-sentry-element="unknown" data-sentry-source-file="PortfolioExportConfigureTemplateForm.tsx">
        <Switch size="small" checked={thresholdEnabled} onChange={isEnabled => {
        setThresholdEnabled(isEnabled);
        if (!isEnabled) {
          onSelect(null);
        } else {
          onSelect(options[0].value);
        }
      }} data-sentry-element="Switch" data-sentry-source-file="PortfolioExportConfigureTemplateForm.tsx" />
      </Form.Item>
      <Form.Item className={`mb-0 ${styles.selectInput} min-w-[220px]`} name={`${props.name}Attribute`} rules={[{
      required: thresholdEnabled,
      message: `Please select an option.`
    }]} help={<div className="text-xs text-gray-500"> {selectedParameter?.note} </div>} data-sentry-element="unknown" data-sentry-source-file="PortfolioExportConfigureTemplateForm.tsx">
        <Select disabled={!thresholdEnabled} size="small" placeholder="Select option" options={options} onChange={handleChange} data-sentry-element="Select" data-sentry-source-file="PortfolioExportConfigureTemplateForm.tsx" />
      </Form.Item>
    </div>;
};
const PortfolioExportConfigureTemplateForm = ({
  handleDone,
  portfolioType,
  availablePortfolioTypes
}: {
  handleDone: () => void;
  portfolioType?: PortfolioRegionType;
  availablePortfolioTypes: string[];
}) => {
  const {
    t
  } = useTranslation("terms");
  const heatThresholdOptions: ThresholdOption[] = [{
    label: t("returnPeriod"),
    value: "heatThresholdReturnPeriod",
    metric: "number",
    note: "System accepts values greater than 1"
  }, {
    label: t("returnFrequency"),
    value: "heatThresholdReturnFrequency",
    metric: "decimal",
    note: "System accepts values 0<x<1"
  }, {
    label: t("temperature"),
    value: "heatThresholdTemperature",
    metric: t("units.celsius"),
    note: "System accepts values between 26 - 52 C"
  }];
  const windThresholdOptions: ThresholdOption[] = [{
    label: t("returnPeriod"),
    value: "windThresholdReturnPeriod",
    metric: "number",
    note: "System accepts values greater than 1"
  }, {
    label: t("returnFrequency"),
    value: "windThresholdReturnFrequency",
    metric: "decimal",
    note: "System accepts values 0<x<1"
  }];
  const tcwThresholdOptions: ThresholdOption[] = [{
    label: t("returnPeriod"),
    value: "tcwThresholdReturnPeriod",
    metric: "number",
    note: "System accepts values greater than 1"
  }, {
    label: t("returnFrequency"),
    value: "tcwThresholdReturnFrequency",
    metric: "decimal",
    note: "System accepts values 0<x<1"
  }, {
    label: t("windSpeed"),
    value: "tcwThresholdWindSpeed",
    metric: t("units.metresPerSecond"),
    note: "System accepts values 0<x<120"
  }];
  const initialFormValues = {
    id: true,
    latitude: true,
    longitude: true,
    address: true,
    elevation: false,
    archetype: true,
    floorHeight: true,
    buildYear: true,
    mortgageRate: true,
    marketValue: true,
    replacementCost: true,
    fireProtection: false,
    foundationDesign: false,
    heatThreshold: false,
    heatThresholdAttribute: heatThresholdOptions[0].value,
    windThreshold: false,
    windThresholdAttribute: windThresholdOptions[0].value,
    tcwThreshold: false,
    tcwThresholdAttribute: tcwThresholdOptions[0].value
  };
  const [form] = Form.useForm();
  const [elevationEnabled, setElevationEnabled] = useState<boolean | undefined>();
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isAllSelected, setIsAllSelected] = useState<boolean>(false);
  const [heatThresholdEnabled, setHeatThresholdEnabled] = useState(initialFormValues.heatThreshold);
  const [tcwThresholdEnabled, setTcwThresholdEnabled] = useState(initialFormValues.tcwThreshold);
  const [windThresholdEnabled, setWindThresholdEnabled] = useState(initialFormValues.windThreshold);
  const isGlobal = portfolioType === "global";
  const templateLabel = availablePortfolioTypes.length > 1 ? isGlobal ? "Advanced Global" : "Advanced HKSAR" : "Advanced";
  const downloadExample = (values: any) => {
    form.validateFields();
    const validKeys = Object.keys(keys);
    const selectedOptionalColumns = Object.keys(values).filter(column => values[column] === true) as PortfolioExportExampleKey[];
    if (values.heatThreshold && values.heatThresholdAttribute) {
      selectedOptionalColumns.push(values.heatThresholdAttribute);
    }
    if (values.windThreshold && values.windThresholdAttribute) {
      selectedOptionalColumns.push(values.windThresholdAttribute);
    }
    if (values.tcwThreshold && values.tcwThresholdAttribute) {
      selectedOptionalColumns.push(values.tcwThresholdAttribute);
    }
    downloadCsvFile(buildPortfolioExportExampleCsv(["id", ...selectedOptionalColumns.filter(opt => validKeys.includes(opt))], portfolioType), {
      filename: templateLabel + " Portfolio Upload Template.csv",
      includeBom: true
    });
    handleDone();
  };
  const validateForm = (values: any) => {
    if (!values.address && !values.latitude) {
      setErrorMessage("Please select at least one: Address, Latitude & Longitude.");
      return;
    }
    if (!values.archetype) {
      setErrorMessage("Please select Archetype. This field is required for analysis.");
      return;
    }
    setErrorMessage("");
  };
  const handleSelectionChange = () => {
    const values = form.getFieldsValue();
    validateForm(values);
    setIsAllSelected(Object.values(values).every(value => value === true || value != ""));
  };
  return <div title="Configure your asset upload template" data-sentry-component="PortfolioExportConfigureTemplateForm" data-sentry-source-file="PortfolioExportConfigureTemplateForm.tsx">
      <p className="mb-2">Customise the asset input settings you wish to provide in your portfolio upload template.</p>
      <Row align="middle" justify="space-between" className="mb-2" data-sentry-element="Row" data-sentry-source-file="PortfolioExportConfigureTemplateForm.tsx">
        <Button type="text" className="px-1" icon={<ArrowLeftOutlined />} onClick={handleDone} data-sentry-element="Button" data-sentry-source-file="PortfolioExportConfigureTemplateForm.tsx">
          Back
        </Button>

        <Checkbox checked={isAllSelected} onChange={e => {
        const checked = e.target.checked;
        form.setFieldsValue({
          latitude: checked,
          longitude: checked,
          address: checked,
          elevation: checked,
          archetype: checked,
          floorHeight: checked,
          buildYear: checked,
          mortgageRate: checked,
          marketValue: checked,
          replacementCost: checked,
          fireProtection: checked,
          foundationDesign: checked,
          heatThreshold: checked,
          windThreshold: checked,
          tcwThreshold: checked
        });
        setIsAllSelected(checked);
        validateForm(form.getFieldsValue());
        setElevationEnabled(!form.getFieldValue("latitude"));
        setHeatThresholdEnabled(checked);
        setWindThresholdEnabled(checked);
        setTcwThresholdEnabled(checked);
      }} data-sentry-element="Checkbox" data-sentry-source-file="PortfolioExportConfigureTemplateForm.tsx">
          {isAllSelected ? "Unselect all" : "Select all"}
        </Checkbox>
      </Row>
      <Form form={form} initialValues={initialFormValues} onFinish={downloadExample} onValuesChange={handleSelectionChange} className={styles.formComponent} data-sentry-element="Form" data-sentry-source-file="PortfolioExportConfigureTemplateForm.tsx">
        <div className="mb-1 font-semibold uppercase">Basic asset input settings</div>
        <div className="mb-2 grid grid-flow-col grid-rows-4 overflow-hidden rounded-md border px-0 text-left text-sm text-gray-500">
          <SwitchInput label={<div>
                <span>{t("address")}</span>
                <div className="text-xs text-gray-500">Required*</div>
              </div>} name="address" data-sentry-element="SwitchInput" data-sentry-source-file="PortfolioExportConfigureTemplateForm.tsx" />

          <SwitchInput label={<div>
                <span>{t("latLong")}</span>
                <div className="text-xs text-gray-500">Required*</div>
              </div>} name="latitude" onSwitchChange={checked => {
          form.setFieldValue("longitude", checked);
          if (checked) {
            setElevationEnabled(false);
          } else {
            setElevationEnabled(true);
            form.setFieldValue("elevation", false);
          }
        }} data-sentry-element="SwitchInput" data-sentry-source-file="PortfolioExportConfigureTemplateForm.tsx" />
          <Form.Item hidden className="!hidden" name={"longitude"} valuePropName="checked" data-sentry-element="unknown" data-sentry-source-file="PortfolioExportConfigureTemplateForm.tsx">
            <Checkbox data-sentry-element="Checkbox" data-sentry-source-file="PortfolioExportConfigureTemplateForm.tsx">Longitude</Checkbox>
          </Form.Item>
          <SwitchInput name={"archetype"} valuePropName="checked" label={<div>
                <ContextualInfoButtonWithText rootClassName="!inline-flex !text-gray-500" id="archetypesExplained" size="small" drawerWidth={750}>
                  {t("archetype")}
                </ContextualInfoButtonWithText>
                <div className="text-xs text-gray-500">Required*</div>
              </div>} data-sentry-element="SwitchInput" data-sentry-source-file="PortfolioExportConfigureTemplateForm.tsx" />
          <SwitchInput name="buildYear" valuePropName="checked" label={<div>
                <ContextualInfoButtonWithText rootClassName="!inline-flex !text-gray-500" id="buildYear" size="small">
                  {t("buildYear")}
                </ContextualInfoButtonWithText>
                <div className="text-xs text-gray-500">Optional</div>
              </div>} data-sentry-element="SwitchInput" data-sentry-source-file="PortfolioExportConfigureTemplateForm.tsx" />
          <SwitchInput name="floorHeight" label={<div>
                <ContextualInfoButtonWithText rootClassName="!inline-flex !text-gray-500" id="heightAboveGround" size="small">
                  {t("heightAboveGround")}
                  <span className="pl-1 text-xs text-gray-500">({t("metres")})</span>
                </ContextualInfoButtonWithText>
                <div className="text-xs text-gray-500">Optional</div>
              </div>} data-sentry-element="SwitchInput" data-sentry-source-file="PortfolioExportConfigureTemplateForm.tsx" />
          <SwitchInput name="marketValue" label={<div>
                <ContextualInfoButtonWithText rootClassName="!inline-flex !text-gray-500" id="marketValue" size="small">
                  {t("marketValue")}
                </ContextualInfoButtonWithText>
                <div className="text-xs text-gray-500">Optional</div>
              </div>} data-sentry-element="SwitchInput" data-sentry-source-file="PortfolioExportConfigureTemplateForm.tsx" />
          <SwitchInput name="replacementCost" label={<div>
                <ContextualInfoButtonWithText rootClassName="!inline-flex !text-gray-500" id="replacementCost" size="small">
                  {t("replacementCost")}
                </ContextualInfoButtonWithText>
                <div className="text-xs text-gray-500">Optional</div>
              </div>} data-sentry-element="SwitchInput" data-sentry-source-file="PortfolioExportConfigureTemplateForm.tsx" />
          <SwitchInput name="mortgageRate" label={<div>
                <ContextualInfoButtonWithText rootClassName="!inline-flex !text-gray-500" id="mortgageRate">
                  {t("mortgageRate")}
                </ContextualInfoButtonWithText>
                <div className="text-xs text-gray-500">Optional</div>
              </div>} data-sentry-element="SwitchInput" data-sentry-source-file="PortfolioExportConfigureTemplateForm.tsx" />
        </div>
        <div className="mb-1 font-semibold uppercase">Advanced asset input settings</div>
        <div className="mb-2 overflow-hidden rounded-md border px-0 text-left text-sm text-gray-500">
          <SwitchInput name="elevation" disabled={elevationEnabled} label={<div>
                <ContextualInfoButtonWithText rootClassName="!inline-flex !text-gray-500" id="elevation" size="small">
                  {t("elevation")}
                </ContextualInfoButtonWithText>
                <div className="text-xs text-gray-500">Optional</div>
              </div>} data-sentry-element="SwitchInput" data-sentry-source-file="PortfolioExportConfigureTemplateForm.tsx" />

          <SwitchInput name="fireProtection" label={<div>
                <ContextualInfoButtonWithText rootClassName="!inline-flex !text-gray-500" id="fireProtection" size="small">
                  {t("fireProtection")}
                </ContextualInfoButtonWithText>
                <div className="text-xs text-gray-500">Optional</div>
              </div>} data-sentry-element="SwitchInput" data-sentry-source-file="PortfolioExportConfigureTemplateForm.tsx" />

          <SwitchInput name="foundationDesign" label={<div>
                <ContextualInfoButtonWithText rootClassName="!inline-flex !text-gray-500" id="foundationDesign" size="small">
                  {t("foundationDesign")}
                </ContextualInfoButtonWithText>
                <div className="text-xs text-gray-500">Optional</div>
              </div>} data-sentry-element="SwitchInput" data-sentry-source-file="PortfolioExportConfigureTemplateForm.tsx" />

          <SelectThresholdParameter name={"heatThreshold"} thresholdEnabled={heatThresholdEnabled} setThresholdEnabled={value => setHeatThresholdEnabled(value)} options={heatThresholdOptions} info={<ContextualInfoButton rootClassName="!inline-flex !text-gray-500 !mt-0" id="heatThreshold" size="small" />} label={<div>
                <span>
                  {t("hazards.heatFailure")} {t("threshold")}
                </span>
                <br />
                <span className="text-xs text-gray-500">Optional</span>
              </div>} onSelect={value => form.setFieldValue("heatThresholdAttribute", value)} data-sentry-element="SelectThresholdParameter" data-sentry-source-file="PortfolioExportConfigureTemplateForm.tsx" />

          <SelectThresholdParameter name={"windThreshold"} thresholdEnabled={windThresholdEnabled} setThresholdEnabled={value => setWindThresholdEnabled(value)} options={windThresholdOptions} info={<ContextualInfoButton rootClassName="!inline-flex !text-gray-500 !mt-0" id="windThreshold" size="small" />} label={<div>
                <span>
                  {t("hazards.extremeWind")} {t("threshold")}
                </span>
                <br />
                <span className="text-xs text-gray-500">Optional</span>
              </div>} onSelect={value => form.setFieldValue("windThresholdAttribute", value)} data-sentry-element="SelectThresholdParameter" data-sentry-source-file="PortfolioExportConfigureTemplateForm.tsx" />

          <SelectThresholdParameter name={"tcwThreshold"} thresholdEnabled={tcwThresholdEnabled} setThresholdEnabled={value => setTcwThresholdEnabled(value)} options={tcwThresholdOptions} info={<ContextualInfoButton rootClassName="!inline-flex !text-gray-500 !mt-0" id="tcwThreshold" size="small" />} label={<div>
                <span>
                  {t("hazards.cycloneWind")} {t("threshold")}
                </span>
                <br />
                <span className="text-xs text-gray-500">Optional</span>
              </div>} onSelect={value => form.setFieldValue("tcwThresholdAttribute", value)} data-sentry-element="SelectThresholdParameter" data-sentry-source-file="PortfolioExportConfigureTemplateForm.tsx" />
        </div>
        {errorMessage && <div className="mb-2 mt-2 text-sm text-red-600">{errorMessage}</div>}
        <div className="mt-4 flex items-center justify-end gap-2">
          <Button type="text" onClick={handleDone} data-sentry-element="Button" data-sentry-source-file="PortfolioExportConfigureTemplateForm.tsx">
            Back
          </Button>
          <Button type="primary" disabled={errorMessage !== ""} icon={<CloudDownloadOutlined />} htmlType="submit" data-sentry-element="Button" data-sentry-source-file="PortfolioExportConfigureTemplateForm.tsx">
            Download Advanced Template
          </Button>
        </div>
      </Form>
    </div>;
};
export default PortfolioExportConfigureTemplateForm;